/**
 * Spinner — the single loading-spinner primitive (Brand Book §1.7 / §4.1).
 *
 * Replaces the four ad-hoc spinners that existed before (Button's inline SVG,
 * react-feather Loader+animate-spin, and two flavours of border spinner). One
 * SVG arc, rotated with Tailwind `animate-spin` (continuous-rotation exception
 * to the 500ms ceiling, like the skeleton shimmer). Colour is inherited via
 * `currentColor`, so callers set the hue with a text-* class.
 *
 * Anti-flicker timing is owned by the primitive so callers never reason about
 * it (NN/g response-time guidance):
 *   - `delayMs` (default 1000): nothing renders for the first second. A request
 *     that finishes under 1s never flashes a spinner. Direct actions that want a
 *     prompt glyph can pass a lower value.
 *   - `minVisibleMs` (default 350): once shown, the spinner stays at least this
 *     long even if loading finishes immediately — so a request landing at 1.1s
 *     doesn't blink a 100ms spinner.
 *
 * For both timers to work the spinner must stay mounted across the loading
 * lifecycle, so the canonical usage is the controlled `active` prop:
 *   <Spinner active={isLoading} />              // always mounted, self-gating
 * A conditionally-mounted `{isLoading && <Spinner />}` still honours `delayMs`
 * but cannot enforce `minVisibleMs` (the parent unmounts it).
 *
 * Reduced motion: the global `prefers-reduced-motion` rule freezes the rotation
 * to a static arc; the `label` (sr-only, or visible via `showLabel`) always
 * conveys the state in text. Render `showLabel` wherever the spinner is the only
 * thing on screen (full-page gates) so a frozen arc never reads as a hang.
 */

import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

const SIZE_PX = { sm: 16, md: 24, lg: 32 } as const;

export interface SpinnerProps {
  /** Arc diameter — keyword (sm 16 · md 24 · lg 32) or an exact px number. */
  size?: keyof typeof SIZE_PX | number;
  /** Accessible label, announced via role="status". */
  label?: string;
  /** Whether something is loading. Drives the delay/min-visible lifecycle. */
  active?: boolean;
  /** Hold-off before the spinner appears (ms). Swallows sub-second waits. */
  delayMs?: number;
  /** Minimum on-screen time once shown (ms). Prevents a flash on fast finishes. */
  minVisibleMs?: number;
  /** Render the label as visible text beside the arc (use on full-page gates). */
  showLabel?: boolean;
  /**
   * Decorative: the arc sits next to visible status text that already conveys
   * the state, so render `aria-hidden` with no role/label (avoids a redundant
   * "Loading" announcement). Use for inline icon-slot spinners.
   */
  decorative?: boolean;
  /** Extra classes on the wrapper (e.g. a text-* colour). */
  className?: string;
}

export function Spinner({
  size = 'md',
  label = 'Loading',
  active = true,
  delayMs = 1000,
  minVisibleMs = 350,
  showLabel = false,
  decorative = false,
  className,
}: SpinnerProps) {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef<number | null>(null);
  const delayTimer = useRef<ReturnType<typeof setTimeout>>();
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(delayTimer.current);
    clearTimeout(hideTimer.current);

    if (active) {
      if (visible) {return;}
      // Wait out the delay window before the spinner ever appears.
      delayTimer.current = setTimeout(() => {
        shownAtRef.current = Date.now();
        setVisible(true);
      }, delayMs);
    } else if (visible) {
      // Loading finished while shown — keep it up for the min-visible tail.
      const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : minVisibleMs;
      const remaining = Math.max(0, minVisibleMs - elapsed);
      hideTimer.current = setTimeout(() => {
        shownAtRef.current = null;
        setVisible(false);
      }, remaining);
    }
    // If !active and !visible we were still in the delay window → nothing to do;
    // clearing the delay timer above already cancelled the pending show.

    return () => {
      clearTimeout(delayTimer.current);
      clearTimeout(hideTimer.current);
    };
  }, [active, visible, delayMs, minVisibleMs]);

  if (!visible) {return null;}

  const px = typeof size === 'number' ? size : SIZE_PX[size];

  const arc = (
    <svg
      className="animate-spin"
      style={{ width: px, height: px }}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  // Decorative: visible status text elsewhere already conveys the state.
  if (decorative) {
    return (
      <span aria-hidden="true" className={clsx('inline-flex items-center', className)}>
        {arc}
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-live="polite"
      className={clsx('inline-flex items-center', showLabel && 'gap-2', className)}
    >
      {arc}
      {showLabel ? (
        <span className="text-body-sm text-txt-secondary">{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </span>
  );
}
